/**
 * The one way the workbench writes a date or a time a person reads (DOM-free, so the host, the
 * renderer and their tests share it).
 *
 * Three preferences, from Settings › General › Date and time (`display.clock`, `display.dateOrder`,
 * `display.weekStart`, the desktop's global layer, which every host reads too):
 *   - the clock: 12-hour ("2:30 pm"), 24-hour ("14:30"), or the computer's region ("system");
 *   - the date order: day-month-year ("26/10/2026", "26 October 2026"), month-day-year
 *     ("10/26/2026", "October 26, 2026"), year-month-day ("2026-10-26", "2026 October 26"), or the region's;
 *   - the first day of the week, for calendars and week strips.
 * "System" is the computer's own choice: the clock, date order and first weekday the person set in Windows
 * (Region settings) or macOS, read by the desktop and handed in as `prefs.system`; what it does not say falls
 * back to the region's locale (`prefs.locale`, the OS's, not the app's language) through Intl.
 *
 * Every function takes the preferences in `opts.prefs`; without them it uses the current ones, which
 * the renderer's store (webview-ui/src/dateTimePrefs.ts) and the host's composition root keep in step
 * with the settings. An unreadable date writes nothing ("" — never "Invalid Date").
 *
 * Nothing else in the workbench calls `toLocaleTimeString`, `toLocaleDateString`, a date's
 * `toLocaleString` or `Intl.DateTimeFormat` to format for people: webview-ui/test/noDirectDateFormat.test.ts
 * and src/test/noDirectDateFormat.test.ts hold every file to that.
 */
/** The preferences with each "system" the computer answered replaced by its answer; the rest stay "system" (the locale's way). */
export function effectiveDateTimePrefs(prefs) {
    const s = prefs.system;
    if (!s)
        return prefs;
    return {
        ...prefs,
        clock: prefs.clock === "system" ? (s.clock ?? "system") : prefs.clock,
        dateOrder: prefs.dateOrder === "system" ? (s.dateOrder ?? "system") : prefs.dateOrder,
        weekStart: prefs.weekStart === "system" ? (s.weekStart ?? "system") : prefs.weekStart,
    };
}
const sameSystem = (a, b) => a?.clock === b?.clock && a?.dateOrder === b?.dateOrder && a?.weekStart === b?.weekStart;
export const CLOCK_CHOICES = ["system", "12h", "24h"];
export const DATE_ORDER_CHOICES = ["system", "dmy", "mdy", "ymd"];
export const WEEK_START_CHOICES = ["system", "monday", "sunday"];
/** The settings, without the `mjuWorkbench.` prefix. */
export const DATE_TIME_KEYS = { clock: "display.clock", dateOrder: "display.dateOrder", weekStart: "display.weekStart" };
export const SYSTEM_DATE_TIME = Object.freeze({ clock: "system", dateOrder: "system", weekStart: "system" });
// ── The current preferences ─────────────────────────────────────────────
let current = SYSTEM_DATE_TIME;
const listeners = new Set();
/** The preferences a call without `opts.prefs` uses. */
export function currentDateTimePrefs() {
    return current;
}
/** What the computer says (its locale and its own clock, date order, first weekday), kept under every preference set after. */
let machine = {};
/**
 * What the computer itself is set to (the desktop reads it from Windows or macOS; a host is handed it at start): kept, and
 * applied under the person's choices, so "Follow system" follows the computer rather than the app's language.
 */
export function setSystemDateTime(next) {
    machine = { ...(next.locale ? { locale: next.locale } : {}), ...(next.system ? { system: { ...next.system } } : {}) };
    setDateTimePrefs({ clock: current.clock, dateOrder: current.dateOrder, weekStart: current.weekStart });
}
/** Take new preferences (the renderer's store, the host's settings); listeners hear a real change only. The computer's locale and choices stay unless `next` names its own. */
export function setDateTimePrefs(given) {
    const next = { ...given, ...(given.locale ?? machine.locale ? { locale: given.locale ?? machine.locale } : {}), ...(given.system ?? machine.system ? { system: given.system ?? machine.system } : {}) };
    if (next.clock === current.clock && next.dateOrder === current.dateOrder && next.weekStart === current.weekStart && next.locale === current.locale && sameSystem(next.system, current.system))
        return;
    current = Object.freeze({ ...next });
    for (const l of listeners)
        l();
}
/** Hear the preferences change; the unsubscribe. */
export function onDateTimePrefsChange(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}
function pick(values, key, choices) {
    const v = values?.[key] ?? values?.[`mjuWorkbench.${key}`];
    return typeof v === "string" && choices.includes(v) ? v : choices[0];
}
/** The preferences from settings values (keys with or without the `mjuWorkbench.` prefix); anything unknown is "system". */
export function dateTimePrefsFrom(values) {
    return {
        clock: pick(values, DATE_TIME_KEYS.clock, CLOCK_CHOICES),
        dateOrder: pick(values, DATE_TIME_KEYS.dateOrder, DATE_ORDER_CHOICES),
        weekStart: pick(values, DATE_TIME_KEYS.weekStart, WEEK_START_CHOICES),
    };
}
// ── Reading a moment ────────────────────────────────────────────────────
const formatters = new Map();
/** One cached Intl formatter per locale and option set (the window formats thousands of dates). */
function fmt(locale, options) {
    const key = `${locale ?? ""}|${JSON.stringify(options)}`;
    let f = formatters.get(key);
    if (!f) {
        f = new Intl.DateTimeFormat(locale, options);
        formatters.set(key, f);
    }
    return f;
}
function toDate(when) {
    if (when === undefined || when === null || when === "")
        return undefined;
    const d = when instanceof Date ? when : new Date(when);
    return Number.isNaN(d.getTime()) ? undefined : d;
}
/** The moment's fields in the computer's zone, or in `timeZone`. */
function partsOf(d, timeZone) {
    if (!timeZone)
        return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(), hour: d.getHours(), minute: d.getMinutes(), second: d.getSeconds() };
    const out = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
    for (const part of fmt("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" }).formatToParts(d)) {
        if (part.type !== "literal")
            out[part.type] = Number(part.value);
    }
    return { year: out.year, month: out.month, day: out.day, hour: out.hour % 24, minute: out.minute, second: out.second };
}
const two = (n) => String(n).padStart(2, "0");
const zone = (timeZone) => (timeZone ? { timeZone } : {});
// ── Times ───────────────────────────────────────────────────────────────
/** "2:30 pm" on a 12-hour clock, "14:30" on a 24-hour one, the region's way on "system"; `seconds` adds them. */
export function time(when, opts = {}) {
    const d = toDate(when);
    if (!d)
        return "";
    const prefs = effectiveDateTimePrefs(opts.prefs ?? current);
    if (prefs.clock === "system")
        return fmt(prefs.locale, { hour: "numeric", minute: "2-digit", ...(opts.seconds ? { second: "2-digit" } : {}), ...zone(opts.timeZone) }).format(d);
    const t = partsOf(d, opts.timeZone);
    const sec = opts.seconds ? `:${two(t.second)}` : "";
    if (prefs.clock === "24h")
        return `${two(t.hour)}:${two(t.minute)}${sec}`;
    const h = t.hour % 12 === 0 ? 12 : t.hour % 12;
    return `${h}:${two(t.minute)}${sec} ${t.hour < 12 ? "am" : "pm"}`;
}
/** A time of day with no date (a schedule's "at", quiet hours): 9, 30 → "9:30 am" or "09:30", the person's way. */
export function timeOfDay(hour, minute, opts = {}) {
    return time(new Date(2000, 0, 1, hour, minute), { ...(opts.prefs ? { prefs: opts.prefs } : {}) });
}
/** A calendar day kept as "YYYY-MM-DD" (no time, no zone), in the person's medium form; the text itself when it is not one. */
export function dayOf(isoDay, opts = {}) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDay);
    return m ? dateMedium(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])), opts) : isoDay;
}
// ── Names ───────────────────────────────────────────────────────────────
/** "Monday" / "Mon". */
export function weekday(when, width = "long", opts = {}) {
    const d = toDate(when);
    return d ? fmt((opts.prefs ?? current).locale, { weekday: width, ...zone(opts.timeZone) }).format(d) : "";
}
/** "October" / "Oct". */
export function monthName(when, width = "long", opts = {}) {
    const d = toDate(when);
    return d ? fmt((opts.prefs ?? current).locale, { month: width, ...zone(opts.timeZone) }).format(d) : "";
}
/** "October 2026" ("2026 October" year-first). */
export function monthYear(when, opts = {}) {
    const d = toDate(when);
    if (!d)
        return "";
    const prefs = effectiveDateTimePrefs(opts.prefs ?? current);
    const width = opts.width ?? "long";
    if (prefs.dateOrder === "system")
        return fmt(prefs.locale, { month: width, year: "numeric", ...zone(opts.timeZone) }).format(d);
    const { year } = partsOf(d, opts.timeZone);
    const month = monthName(d, width, opts);
    return prefs.dateOrder === "ymd" ? `${year} ${month}` : `${month} ${year}`;
}
// ── Dates ───────────────────────────────────────────────────────────────
/** "26/10/2026", "10/26/2026" or "2026-10-26"; the region's numeric date on "system". */
export function dateShort(when, opts = {}) {
    const d = toDate(when);
    if (!d)
        return "";
    const prefs = effectiveDateTimePrefs(opts.prefs ?? current);
    if (prefs.dateOrder === "system")
        return fmt(prefs.locale, { day: "numeric", month: "numeric", year: "numeric", ...zone(opts.timeZone) }).format(d);
    const t = partsOf(d, opts.timeZone);
    if (prefs.dateOrder === "ymd")
        return `${t.year}-${two(t.month)}-${two(t.day)}`;
    return prefs.dateOrder === "mdy" ? `${two(t.month)}/${two(t.day)}/${t.year}` : `${two(t.day)}/${two(t.month)}/${t.year}`;
}
function namedDate(d, width, opts) {
    const prefs = effectiveDateTimePrefs(opts.prefs ?? current);
    const t = partsOf(d, opts.timeZone);
    const withYear = opts.year === "auto" ? partsOf(new Date(opts.now ?? Date.now()), opts.timeZone).year !== t.year : opts.year !== false;
    if (prefs.dateOrder === "system") {
        return fmt(prefs.locale, { day: "numeric", month: width, ...(withYear ? { year: "numeric" } : {}), ...(opts.weekday ? { weekday: width } : {}), ...zone(opts.timeZone) }).format(d);
    }
    const month = monthName(d, width, opts);
    const day = opts.weekday ? weekday(d, width, opts) : "";
    if (prefs.dateOrder === "mdy") {
        const md = `${month} ${t.day}${withYear ? `, ${t.year}` : ""}`;
        return day ? `${day}, ${md}` : md;
    }
    const body = prefs.dateOrder === "ymd" ? `${withYear ? `${t.year} ` : ""}${month} ${t.day}` : `${t.day} ${month}${withYear ? ` ${t.year}` : ""}`;
    return day ? `${day} ${body}` : body;
}
/** "26 Oct 2026", "Oct 26, 2026" or "2026 Oct 26" — the short month name; see {@link NamedDateOpts}. */
export function dateMedium(when, opts = {}) {
    const d = toDate(when);
    return d ? namedDate(d, "short", opts) : "";
}
/** "26 October 2026", "October 26, 2026" or "2026 October 26"; with `weekday`, "Monday 26 October 2026". */
export function dateLong(when, opts = {}) {
    const d = toDate(when);
    return d ? namedDate(d, "long", opts) : "";
}
/** A date and its time: "26/10/2026, 2:30 pm"; `date: "medium"` → "26 Oct 2026, 2:30 pm", `"long"` → "26 October 2026, 2:30 pm". */
export function dateTime(when, opts = {}) {
    const d = toDate(when);
    if (!d)
        return "";
    const date = opts.date === "medium" ? dateMedium(d, opts) : opts.date === "long" ? dateLong(d, opts) : dateShort(d, opts);
    return `${date}, ${time(d, opts)}`;
}
/** "just now", "5 min ago", "3 h ago", "yesterday", "4 days ago", then the date ({@link dateShort}). */
export function relative(when, now = Date.now(), opts = {}) {
    const d = toDate(when);
    if (!d)
        return "";
    const mins = Math.floor((now - d.getTime()) / 60_000);
    if (mins < 1)
        return "just now";
    if (mins < 60)
        return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24)
        return `${hours} h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1)
        return "yesterday";
    if (days < 7)
        return `${days} days ago`;
    return dateShort(d, opts);
}
// ── Weeks ───────────────────────────────────────────────────────────────
/** Regions whose weeks start on Sunday, for a runtime that cannot say (Intl.Locale week info is newer than some). */
const SUNDAY_REGIONS = new Set(["US", "CA", "MX", "BR", "JP", "KR", "TW", "HK", "IL", "PH", "IN", "ZA", "SA", "PE", "CO", "VE", "GT", "HN", "PA", "DO", "PR", "SV", "NI", "BZ", "BS", "JM", "TT", "ZW", "KE", "ET", "TH", "KH", "LA", "MO", "PK", "BD", "NP", "ID", "SG", "AR"]);
/** The first day of the week: 0 Sunday, 1 Monday (as `Date#getDay`); "system" asks the region. */
export function weekStartDay(given = current) {
    const prefs = effectiveDateTimePrefs(given);
    if (prefs.weekStart === "monday")
        return 1;
    if (prefs.weekStart === "sunday")
        return 0;
    try {
        const tag = prefs.locale ?? fmt(undefined, {}).resolvedOptions().locale;
        const loc = new Intl.Locale(tag);
        const info = typeof loc.getWeekInfo === "function" ? loc.getWeekInfo() : loc.weekInfo;
        if (info && typeof info.firstDay === "number")
            return info.firstDay % 7;
        const region = loc.maximize().region;
        return region && SUNDAY_REGIONS.has(region) ? 0 : 1;
    }
    catch {
        return 1;
    }
}
/** This computer's IANA time zone ("Australia/Sydney"), for saying which clock a time is on. */
export function localTimeZone() {
    try {
        return fmt(undefined, {}).resolvedOptions().timeZone;
    }
    catch {
        return "UTC";
    }
}
/** The line under Settings › Date and time: "Today at 2:30 pm · 26/10/2026". */
export function dateTimeExample(now, prefs = current) {
    return `Today at ${time(now, { prefs })} · ${dateShort(now, { prefs })}`;
}
//# sourceMappingURL=dateTime.js.map